import {
  Badge,
  Button,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { CommercialConsultant } from '../../../services/CommercialConsultant/types'
import CommercialConsultantDeleteDialog from './CommercialConsultantDeleteDialog'
import CommercialConsultantFormModal from './CommercialConsultantFormModal'

const getLabelsOfAttendanceType = (consultant: CommercialConsultant) => {
  const [onlineRecorded, onlineLive] = [
    consultant.is_online_recorded_course,
    consultant.is_online_live_course
  ]

  if (!onlineRecorded && !onlineLive) {
    return 'Nenhuma categoria'
  }

  const labels = []
  if (onlineLive) labels.push(<Badge colorScheme="purple">Presencial/Ao Vivo</Badge>)
  if (onlineRecorded) labels.push(<Badge colorScheme="blue">Gravado</Badge>)

  return labels
}

export default function CommercialConsultantsPage() {
  const service = new CommercialConsultantService()
  const toast = useToast()
  const [consultants, setConsultants] = useState<CommercialConsultant[]>([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState<CommercialConsultant[]>([])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<CommercialConsultant | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<CommercialConsultant | null>(null)
  const [isUpdating, setIsUpdating] = useState<number[]>([])
  const cancelRef = useRef(null)

  useEffect(() => {
    loadConsultants()
  }, [])

  useEffect(() => {
    setFiltered(
      consultants.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase())
      )
    )
  }, [search, consultants])

  const loadConsultants = async () => {
    setConsultants(await service.list())
  }

  const handleUpdateEnabled = async (enabled: boolean, id: number) => {
    setIsUpdating((prev) => [...prev, id])

    await service.updateEnabled(id, enabled)

    toast({
      title: 'Sucesso',
      description: `Consultor ${enabled ? 'ativado' : 'desativado'} com sucesso`,
      status: 'success',
      duration: 3000,
      isClosable: true
    })

    setConsultants((prev) =>
      prev.map((consultant) => {
        if (consultant.id === id) {
          return { ...consultant, enabled }
        }
        return consultant
      })
    )

    setTimeout(() => {
      setIsUpdating((prev) => prev.filter((i) => i !== id))
    }, 1000)
  }

  return (
    <>
      <Heading as="h1" size="lg" mb="10" textTransform="uppercase">
        Consultores Comerciais
      </Heading>
      <Flex flex="1" justifyContent="flex-end" alignItems="center">
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Categorias</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  Nenhum consultor encontrado
                </Td>
              </Tr>
            ) : (
              filtered.map((consultant) => (
                <Tr key={consultant.id}>
                  <Td>{consultant.id}</Td>
                  <Td>{consultant.name}</Td>
                  <Td>{consultant.email}</Td>
                  <Td>
                    <Flex gap={2}>{getLabelsOfAttendanceType(consultant)}</Flex>
                  </Td>
                  <Td>
                    <Switch
                      isChecked={consultant.enabled}
                      colorScheme="green"
                      size="sm"
                      onChange={(e) => handleUpdateEnabled(e.target.checked, consultant.id)}
                      isDisabled={isUpdating.includes(consultant.id)}
                    />
                  </Td>
                  <Td>
                    <Tooltip label="Editar">
                      <IconButton
                        aria-label="Editar"
                        icon={<i className="lni lni-pencil" />}
                        colorScheme="blue"
                        size="sm"
                        mr={2}
                        onClick={() => {
                          setEditing(consultant)
                          setIsFormOpen(true)
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Excluir">
                      <IconButton
                        aria-label="Excluir"
                        icon={<i className="lni lni-trash-can" />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => {
                          setDeleting(consultant)
                          setIsDeleteOpen(true)
                        }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <CommercialConsultantFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadConsultants()
        }}
        consultant={editing}
      />
      <CommercialConsultantDeleteDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        consultant={deleting}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          setIsDeleteOpen(false)
          loadConsultants()
        }}
      />

      <Tooltip label="Adicionar " aria-label="Adicionar " placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>
    </>
  )
}
